import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { isType, match, partial, variant } from 'variant';
import type { VariantOf } from 'variant';
import { Box, InputState, SelectGroupsInput } from '@wix/cli-ui-kit';
import type { SelectOption, SelectGroup } from '@wix/cli-ui-kit';
import { useWixCliAstroBi } from '../../bi/index.js';
import { useGenerateDashboardPage } from './dashboard-page/index.js';
import { useGenerateDashboardModal } from './dashboard-modal/DashboardModal.js';
import { useGenerateServicePlugin } from './service-plugin/index.js';
import { DashboardPlugin } from './plugins/DashboardPlugin.js';
import { useGenerateEvent } from './event/index.js';
import { useWizardStep, Wizard } from './Wizard.js';

const DevCenterGenerateFlow = variant({
  DashboardPage: {},
  DashboardPlugin: {},
  DashboardMenuPlugin: {},
  DashboardModal: {},
  Event: {},
  ServicePlugin: {},
});

export type GenerateFlow = VariantOf<typeof DevCenterGenerateFlow>;

const FlowSelector: FC<{
  onSubmit: (flow: SelectOption<GenerateFlow>) => void;
  generateFlow: SelectOption<GenerateFlow>;
  generateFlows: Array<SelectGroup<GenerateFlow>>;
}> = ({ onSubmit, generateFlow, generateFlows }) => {
  const bi = useWixCliAstroBi();
  const { goToNextStep, isActive } = useWizardStep();

  const selectFlow = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      bi.cliFlowStepAnswered({
        question: 'What kind of extension would you like to generate?',
        questionKey: 'generate_command.flow.choose_flow',
        answer: selectedType.title,
      });
      onSubmit(selectedType);
      goToNextStep();
    },
    [bi, onSubmit, goToNextStep]
  );

  useEffect(() => {
    goToNextStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SelectGroupsInput<GenerateFlow>
      value={
        !isActive
          ? InputState.Submitted(generateFlow)
          : InputState.NotSubmitted(generateFlow)
      }
      label={'What kind of extension would you like to generate?'}
      groups={generateFlows}
      onSubmit={selectFlow}
      maxHeight={15}
    />
  );
};

export const GenerateCommand: FC = () => {
  const bi = useWixCliAstroBi();

  const dashboardPageSteps = useGenerateDashboardPage();
  const dashboardModalSteps = useGenerateDashboardModal();
  const generateEventsSteps = useGenerateEvent();
  const generateServicePluginSteps = useGenerateServicePlugin();

  const [generateFlow, setGenerateFlow] =
    useState<SelectOption<GenerateFlow>>();

  const generateFlows: Array<SelectGroup<GenerateFlow>> = useMemo(
    () => [
      {
        title: 'Dashboard',
        items: [
          {
            key: 'dashboard-page',
            title: 'Dashboard Page',
            value: DevCenterGenerateFlow.DashboardPage(),
          },
          {
            key: 'dashboard-modal',
            title: 'Dashboard Modal',
            value: DevCenterGenerateFlow.DashboardModal(),
          },
          {
            key: 'dashboard-plugin',
            title: 'Dashboard Plugin',
            value: DevCenterGenerateFlow.DashboardPlugin(),
          },
          {
            key: 'dashboard-menu-plugin',
            title: 'Dashboard Menu Plugin',
            value: DevCenterGenerateFlow.DashboardMenuPlugin(),
          },
        ],
      },
      {
        title: 'Backend',
        items: [
          {
            key: 'event',
            title: 'Event',
            value: DevCenterGenerateFlow.Event(),
          },
          {
            key: 'service-plugin',
            title: 'Service Plugin',
            value: DevCenterGenerateFlow.ServicePlugin(),
          },
        ],
      },
    ],
    []
  );

  const selectFlow = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      bi.cliFlowStepAnswered({
        question: 'What kind of extension would you like to generate?',
        questionKey: 'generate_command.flow.choose_flow',
        answer: selectedType.title,
      });
      setGenerateFlow(selectedType);
    },
    [bi]
  );

  const shouldEditFlow =
    isType(generateFlow?.value, 'DashboardPage') ||
    isType(generateFlow?.value, 'DashboardModal') ||
    isType(generateFlow?.value, 'ServicePlugin') ||
    isType(generateFlow?.value, 'Event');

  return (
    <Box flexDirection="column" paddingTop={1}>
      {!shouldEditFlow && (
        <SelectGroupsInput<GenerateFlow>
          value={
            generateFlow
              ? InputState.Submitted(generateFlow)
              : InputState.NotSubmitted(generateFlow)
          }
          label={'What kind of extension would you like to generate?'}
          groups={generateFlows}
          onSubmit={selectFlow}
          maxHeight={15}
        />
      )}
      {generateFlow &&
        match(
          generateFlow.value,
          partial({
            DashboardPage: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...dashboardPageSteps,
                ]}
              />
            ),
            DashboardModal: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...dashboardModalSteps,
                ]}
              />
            ),
            ServicePlugin: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateServicePluginSteps,
                ]}
              />
            ),
            Event: () => (
              <Wizard
                name={generateFlow.value.type}
                steps={[
                  <FlowSelector
                    generateFlows={generateFlows}
                    generateFlow={generateFlow}
                    onSubmit={setGenerateFlow}
                  />,
                  ...generateEventsSteps,
                ]}
              />
            ),
            DashboardPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            DashboardMenuPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            default: () => null,
          })
        )}
    </Box>
  );
};
